import React from "react";
import PropTypes from "prop-types";
import { Input, Select, Button, Form, Icon } from "antd";
import { debounce } from "lodash";
import sorters from "utils/sorters";
import { EMPLOYEE_ACTIVE_DELIMITER } from "config";

export const filterClientIndustryReport = (props, changedValues, values) => {
  const { responses, onSearch, setSettings } = props;

  const uppercaseTerm = values.searchTerm
    ? values.searchTerm.toUpperCase()
    : "";
  const practices = values.selectedPractices;
  const positions = values.selectedPositions;
  const offices = values.selectedOffices;
  const clientIndustry = values.selectedClientIndustry;

  setSettings({
    search: values.searchTerm,
    practice: practices,
    position: positions,
    office: offices,
    clientIndustry: clientIndustry
  });

  const filteredResponses = responses.filter(r => {
    let isNameValid =
      !uppercaseTerm ||
      !uppercaseTerm.length ||
      r.name.toUpperCase().indexOf(uppercaseTerm) > -1;
    let isPracticeValid =
      !practices || !practices.length || practices.indexOf(r.practice) > -1;
    let isPositionValid =
      !positions || !positions.length || positions.indexOf(r.position) > -1;
    let isOfficeValid =
      !offices || !offices.length || offices.indexOf(r.office) > -1;
    let isClientIndustryValid =  
      !clientIndustry || 
      !clientIndustry.length || 
      clientIndustry.some(ci => r.clientIndustry.includes(ci));
    return isNameValid && isPracticeValid && isPositionValid && isOfficeValid && isClientIndustryValid;
  });
  onSearch(filteredResponses);
};

const ClientIndustryReportFilters = props => {
  const { responses = [], form, settings, searchRef, disabled } = props;
  const practices = [
    ...new Set(
      responses
        .sort((a, b) => sorters.string(a.practice, b.practice))
        .map(r => r.practice)
        .filter(r => r !== undefined)
    )
  ];
  const positions = [
    ...new Set(
      responses
        .sort((a, b) => sorters.string(a.position, b.position))
        .map(r => r.position)
        .filter(r => r !== undefined)
    )
  ];
  const offices = [
    ...new Set(
      responses
        .sort((a, b) => sorters.string(a.office, b.office))
        .map(r => r.office)
        .filter(r => r !== undefined)
    )
  ];
  const clientIndustry = [  
    ...new Set(
      responses
        .sort((a, b) => sorters.string(a.clientIndustry, b.clientIndustry))
        .flatMap(r => r.clientIndustry.split(EMPLOYEE_ACTIVE_DELIMITER))
        .map(r => r.trim()) 
        .filter(r => r !== undefined && r !== "")
    )
  ];

  const clearFilters = () => {
    form.resetFields();
    filterClientIndustryReport(
      props,
      {},
      {
        searchTerm: "",
        selectedPractices: [],
        selectedPositions: [],
        selectedOffices: [],
        selectedClientIndustry: []
      }
    );
  };

  const { getFieldDecorator } = form;

  const selectedPractices = form.getFieldValue("selectedPractices") || [];
  const selectedPositions = form.getFieldValue("selectedPositions") || [];
  const selectedOffices = form.getFieldValue("selectedOffices") || [];
  const selectedClientIndustry = form.getFieldValue("selectedClientIndustry") || [];

  return (
    <div style={disabled ? { pointerEvents: "none", opacity: 0.4 } : {}}>
      <Form layout="inline">
        <Form.Item>
          {getFieldDecorator("searchTerm", {
            initialValue: settings.search
          })(
            <Input
              prefix={<Icon type="filter" />}
              ref={searchRef}
              disabled={disabled}
              placeholder="Filter"
            />
          )}
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("selectedPractices", {
            initialValue: settings.practice
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedPractices.length} Practice${
                selectedPractices.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Practice"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {practices.map(p => (
                <Select.Option key={p} value={p}>
                  {p}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("selectedPositions", {
            initialValue: settings.position
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedPositions.length} Position${
                selectedPositions.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Position"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {positions.map(p => (
                <Select.Option key={p} value={p}>
                  {p}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("selectedOffices", {
            initialValue: settings.office
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedOffices.length} Office${
                selectedOffices.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Location"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {offices.map(o => (
                <Select.Option key={o} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("selectedClientIndustry", {
            initialValue: settings.clientIndustry
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedClientIndustry.length} Client Industry${
                selectedClientIndustry.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Client Industry"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {clientIndustry.map(c => (
                <Select.Option key={c} value={c}>
                  {c}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item>
          <Button
            title="Clear filters"
            icon="close"
            type="link"
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

ClientIndustryReportFilters.propTypes = {
  onSearch: PropTypes.func.isRequired,
  responses: PropTypes.array
};

export default Form.create({
  name: "filter_client_industry_report_form",
  onValuesChange: debounce(filterClientIndustryReport, 500)
})(ClientIndustryReportFilters);